import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { TabTrigger } from "../components/CoacheeTabTriggers";
import CoacheeInfoPanel from "../components/CoacheeInfoPanel";
import CoacheeDocumentsPanel from "../components/CoacheeDocumentsPanel";
import CoacheeTestsPanel from "../components/CoacheeTestsPanel";
import VerslagleggingCoachPanel from "../components/VerslagleggingCoachPanel";

/**
 * Coach's detail-view for a single coachee. Pure orchestrator:
 *  - Loads /coachees/{id} + /survey/result/{id} in parallel
 *  - Renders 4 outer tabs (Info / CV en Profielen / Tests / Verslaglegging)
 *  - Each tab content delegates to its own self-contained Panel component
 *
 * Previously this file was ~342 lines with all panels inlined. The split
 * was done in the 2026-06 frontend refactor (see PRD changelog).
 */
export default function CoacheeDetailPage() {
  const { coacheeId } = useParams();
  const [coachee, setCoachee] = useState(null);
  const [surveyResult, setSurveyResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get(`/coachees/${coacheeId}`),
        api.get(`/survey/result/${coacheeId}`),
      ]);
      setCoachee(c.data);
      setSurveyResult(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coacheeId]);

  return (
    <AppLayout
      title={coachee?.name || "Coachee"}
      breadcrumbs={[{ label: "Dashboard", to: "/dashboard" }, { label: coachee?.name || "..." }]}
    >
      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : !coachee ? (
        <div className="text-zinc-500">Coachee niet gevonden.</div>
      ) : (
        <Tabs.Root value={tab} onValueChange={setTab}>
          <Tabs.List className="flex flex-wrap border-b border-zinc-200 mb-6" data-testid="tabs-outer">
            <TabTrigger value="info" testid="tab-info">Info</TabTrigger>
            <TabTrigger value="documents" testid="tab-documents">CV en Profielen</TabTrigger>
            <TabTrigger value="tests" testid="tab-tests">Tests</TabTrigger>
            <TabTrigger value="reports" testid="tab-reports">Verslaglegging</TabTrigger>
          </Tabs.List>

          <Tabs.Content value="info">
            <CoacheeInfoPanel coachee={coachee} onSaved={fetchAll} />
          </Tabs.Content>

          <Tabs.Content value="documents">
            <CoacheeDocumentsPanel coacheeId={coacheeId} coacheeName={coachee.name} />
          </Tabs.Content>

          <Tabs.Content value="tests">
            <CoacheeTestsPanel
              coachee={coachee}
              coacheeId={coacheeId}
              coacheeName={coachee.name}
              surveyResult={surveyResult}
            />
          </Tabs.Content>

          <Tabs.Content value="reports">
            <VerslagleggingCoachPanel coacheeId={coacheeId} coacheeName={coachee.name} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </AppLayout>
  );
}
